import {PlayButton} from '@/assets/icons/icons'
import {Media} from '@/components/ui/media/Media'
import {SplideType} from '@/components/ui/profile-carousel/ProfileCarousel'
import BlurhashCanvas from '@/features/chat/components/ChatMessage/message-types/PaidMediaMessage/components/BlurhashCanvas'
import {RestrictionLayer} from '@/features/feed/components/host-feed-mural/components/restriction-layer/RestrictionLayer'
import {UPDATE_VARIANT} from '@/features/feed/queries/useGetContentById'
import {HostFeedCardV3} from '@/features/feed/types'
import {
    mediaStyles,
    StyledMediaWrapper,
    StyledPlayButtonWrapper,
    StyledShadow,
    StyledUnpaidContent
} from '@/features/my-moments/components/moment-item/moment-preview/style'
import {isValidBlurhash} from '@/features/profile/helpers/helpers'
import {MURAL_TYPES} from '@/features/profile/services/completeProfile.schema'
import {MediaPackageContentV3} from '@/features/profile/services/types'
import {MediaTypeE} from '@/types'
import {checkMediaExtension} from '@/utilities/helpers'
import {Splide, SplideSlide} from '@splidejs/react-splide'
import {FC} from 'react'

export type HostProfileMuralPreviewProps = {
    post?: HostFeedCardV3
    muralType: MURAL_TYPES
    type?: MediaTypeE
    url?: string | null
    isPaid?: boolean
    isAdultContent?: boolean
    isBlurred?: boolean
    contentId: string
    defaultItem?: {url: string; muralType: MURAL_TYPES}
    variant: UPDATE_VARIANT
    extraUnpaidContent?: () => JSX.Element
    mediaPackagesContents?: MediaPackageContentV3[]
    onChangeCurrentMediaPackageIndex?: (currentIndex: number) => void
}

const HostProfileMuralPreview: FC<HostProfileMuralPreviewProps> = ({
    post,
    muralType,
    url,
    isPaid,
    isAdultContent = false,
    isBlurred = false,
    variant,
    defaultItem,
    mediaPackagesContents,
    contentId,
    extraUnpaidContent,
    onChangeCurrentMediaPackageIndex
}) => {
    const content = getContentByMuralType({
        post,
        muralType,
        url,
        isBlurred,
        variant,
        isPaid,
        contentId,
        defaultItem,
        mediaPackagesContents,
        isRestrictedContent: isAdultContent,
        extraUnpaidContent,
        onChangeCurrentMediaPackageIndex
    })

    return <StyledShadow isPaid={isPaid}>{content}</StyledShadow>
}

export default HostProfileMuralPreview

function getContentByMuralType({
    muralType,
    url,
    isPaid,
    isBlurred,
    defaultItem,
    contentId,
    variant,
    mediaPackagesContents,
    isRestrictedContent = false,
    extraUnpaidContent,
    onChangeCurrentMediaPackageIndex
}: HostProfileMuralPreviewProps & {
    isRestrictedContent?: boolean
    isBlurred: boolean
    variant: UPDATE_VARIANT
}): JSX.Element {
    const correctType: MURAL_TYPES | undefined = url
        ? checkMediaExtension(url)?.type === MediaTypeE.IMAGE
            ? MURAL_TYPES.PHOTOS
            : MURAL_TYPES.VIDEOS
        : undefined

    const paid = () => {
        switch (muralType) {
            case MURAL_TYPES.VIDEOS:
                return (
                    <StyledMediaWrapper>
                        <StyledPlayButtonWrapper>
                            <PlayButton width={70} height={70} />
                        </StyledPlayButtonWrapper>
                        {isBlurred ? (
                            <BlurhashCanvas
                                hash={String(url)}
                                width={190}
                                height={178}
                                punch={0.1}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: '46px'
                                }}
                            />
                        ) : (
                            <video>
                                <source src={`${url}#t=0.001`} />
                            </video>
                        )}
                    </StyledMediaWrapper>
                )

            case MURAL_TYPES.MEDIA_PACKAGES:
                return (
                    <Splide
                        options={{
                            arrows: false,
                            pagination: true
                        }}
                        onMove={(_: SplideType, newIndex: number) => {
                            onChangeCurrentMediaPackageIndex?.(newIndex)
                        }}
                    >
                        {mediaPackagesContents?.map(item => (
                            <SplideSlide key={item.id}>
                                <RestrictionLayer
                                    isRestricted={item.isAdultContent}
                                    mediaType={MURAL_TYPES.MEDIA_PACKAGES}
                                    mediaId={contentId.toString()}
                                    variant={variant}
                                    isBlurred={isBlurred}
                                    creatorId={String((item as MediaPackageContentV3).userId)}
                                >
                                    {item.type === 'image' ? (
                                        item.url ? (
                                            <img src={item.url as string} alt={item.url as string} />
                                        ) : (
                                            <BlurhashCanvas
                                                hash={String(item.blurHash)}
                                                width={190}
                                                height={178}
                                                punch={0.1}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    borderRadius: '46px'
                                                }}
                                            />
                                        )
                                    ) : (
                                        <StyledMediaWrapper>
                                            <StyledPlayButtonWrapper>
                                                <PlayButton width={70} height={70} />
                                            </StyledPlayButtonWrapper>
                                            {item.url ? (
                                                <video>
                                                    <source src={`${item.url as string}#t=0.001`} />
                                                </video>
                                            ) : (
                                                <BlurhashCanvas
                                                    hash={String(item.blurHash)}
                                                    width={190}
                                                    height={178}
                                                    punch={0.1}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        borderRadius: '46px'
                                                    }}
                                                />
                                            )}
                                        </StyledMediaWrapper>
                                    )}
                                </RestrictionLayer>
                            </SplideSlide>
                        ))}
                    </Splide>
                )

            default:
                return isBlurred ? (
                    <BlurhashCanvas
                        hash={String(url)}
                        width={190}
                        height={178}
                        punch={0.1}
                        style={{
                            width: '100%',
                            height: '100%',
                            borderRadius: '46px'
                        }}
                    />
                ) : (
                    <Media imageUrl={url} mediaStyles={mediaStyles} />
                )
        }
    }

    const unpaid = () => {
        switch (muralType) {
            case MURAL_TYPES.VIDEOS:
                return (
                    <>
                        <StyledUnpaidContent>{extraUnpaidContent?.()}</StyledUnpaidContent>
                        <DefaultItem
                            defaultItem={
                                defaultItem || (url && correctType ? {url, muralType: correctType} : undefined)
                            }
                            enabled
                        >
                            <></>
                        </DefaultItem>
                    </>
                )
            case MURAL_TYPES.PHOTOS:
                return (
                    <>
                        <StyledUnpaidContent>{extraUnpaidContent?.()}</StyledUnpaidContent>
                        <DefaultItem
                            defaultItem={
                                defaultItem || (url && correctType ? {url, muralType: correctType} : undefined)
                            }
                            enabled
                        >
                            <></>
                        </DefaultItem>
                    </>
                )

            case MURAL_TYPES.MEDIA_PACKAGES:
                return (
                    <>
                        <StyledUnpaidContent>{extraUnpaidContent?.()}</StyledUnpaidContent>
                        <BlurhashCanvas
                            hash={url ?? ''}
                            width={190}
                            height={178}
                            punch={0.1}
                            style={{width: '100%', height: '100%', borderRadius: '46px'}}
                        />
                    </>
                )

            default:
                return null
        }
    }

    return isPaid ? (
        muralType === MURAL_TYPES.MEDIA_PACKAGES ? (
            paid()
        ) : (
            <RestrictionLayer
                isRestricted={isRestrictedContent}
                mediaType={muralType}
                mediaId={contentId}
                variant={variant}
                isBlurred={isBlurred}
            >
                {paid()}
            </RestrictionLayer>
        )
    ) : (
        unpaid() ?? <></>
    )
}

const DefaultItem = ({
    children = <></>,
    defaultItem,
    enabled
}: {
    children?: JSX.Element
    defaultItem?: {url: string; muralType: MURAL_TYPES}
    enabled: boolean
}) => {
    if (!defaultItem || !enabled) return children
    const isBlurHash = isValidBlurhash(defaultItem.url)
    switch (defaultItem.muralType) {
        case MURAL_TYPES.VIDEOS:
            if (isBlurHash) {
                return (
                    <BlurhashCanvas
                        hash={defaultItem.url}
                        width={190}
                        height={178}
                        punch={0.1}
                        style={{
                            width: '100%',
                            height: '100%',
                            borderRadius: '46px'
                        }}
                    />
                )
            }

            return (
                <StyledMediaWrapper>
                    <video>
                        <source src={`${defaultItem.url}#t=0.001`} />
                    </video>
                </StyledMediaWrapper>
            )

        case MURAL_TYPES.PHOTOS:
            return isBlurHash ? (
                <BlurhashCanvas
                    hash={defaultItem.url}
                    width={190}
                    height={178}
                    punch={0.1}
                    style={{
                        width: '100%',
                        height: '100%',
                        borderRadius: '46px'
                    }}
                />
            ) : (
                <Media imageUrl={defaultItem.url} mediaStyles={mediaStyles} className="blur" />
            )

        default:
            return children
    }
}
